/* Helpers de Web Push del club Ferroviarios.
 * - registerSW: registra /push-sw.js
 * - getPushStatus: notSupported | denied | granted | default
 * - subscribeUser / unsubscribeUser
 *
 * NO lanza excepciones al caller — todos los helpers devuelven objetos con
 * `ok:bool` y `reason?:string` para que la UI decida qué mostrar.
 */
import api from "@/lib/api";

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - base64String.length % 4) % 4);
  const b64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(b64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) out[i] = raw.charCodeAt(i);
  return out;
}

export function isPushSupported() {
  return typeof window !== "undefined"
    && "serviceWorker" in navigator
    && "PushManager" in window
    && "Notification" in window;
}

export function currentPermission() {
  if (!("Notification" in window)) return "notSupported";
  return Notification.permission; // "default" | "granted" | "denied"
}

let _swReg = null;
export async function registerSW() {
  if (!isPushSupported()) return null;
  if (_swReg) return _swReg;
  try {
    _swReg = await navigator.serviceWorker.register("/push-sw.js", { scope: "/" });
    return _swReg;
  } catch (e) {
    console.warn("SW register failed", e);
    return null;
  }
}

export async function subscribeUser() {
  if (!isPushSupported()) return { ok: false, reason: "notSupported" };
  const perm = await Notification.requestPermission();
  if (perm !== "granted") return { ok: false, reason: perm };
  const reg = await registerSW();
  if (!reg) return { ok: false, reason: "swFailed" };
  let key;
  try {
    const r = await api.get("/push/vapid_public_key");
    key = r.data.public_key;
  } catch (e) {
    return { ok: false, reason: "vapidFetch" };
  }
  let sub;
  try {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(key),
    });
  } catch (e) {
    return { ok: false, reason: "subscribeFailed", detail: String(e) };
  }
  const asJson = sub.toJSON();
  try {
    await api.post("/push/subscribe", {
      endpoint: asJson.endpoint,
      keys: asJson.keys,
      user_agent: navigator.userAgent,
    });
  } catch (e) {
    return { ok: false, reason: "backendSave" };
  }
  return { ok: true };
}

export async function unsubscribeUser() {
  const reg = await registerSW();
  if (!reg) return { ok: false };
  const sub = await reg.pushManager.getSubscription();
  if (sub) {
    try { await api.post("/push/unsubscribe", { endpoint: sub.endpoint }); } catch (_) {}
    try { await sub.unsubscribe(); } catch (_) {}
  }
  return { ok: true };
}

/** Recibe { type: "push-nav", url } desde el SW cuando el usuario clickea
 *  una notificación con la app ya abierta. Devuelve un cleanup fn. */
export function onPushNav(handler) {
  if (!("serviceWorker" in navigator)) return () => {};
  const fn = (ev) => {
    if (ev.data && ev.data.type === "push-nav" && ev.data.url) handler(ev.data.url);
  };
  navigator.serviceWorker.addEventListener("message", fn);
  return () => navigator.serviceWorker.removeEventListener("message", fn);
}
